import { useEffect, useRef, useState } from 'react';
import { ShopifyProductMatchMapping } from '@domain/connection/models/ConnectionSettings';
import { getDuplicateSourceValues, ShopifyMatchedJuststockProduct, ShopifySetupProductPreview } from '@domain/connection/models/ShopifyMatchPreview';
import { ProductPreviewState } from '@features/connections/setup/productPreviewContext';
import { container } from '@infrastructure/container';
import { SHOPIFY_MATCH_SOURCE_FIELDS } from '@shared/connection/shopify/constants/shopifyProductFields';

interface UseShopifyProductPreviewArgs {
  connectionId?: string;
  mapping: ShopifyProductMatchMapping;
  onMappingChange: (mapping: ShopifyProductMatchMapping) => void;
  enabled?: boolean;
}

export function useShopifyProductPreview({
  connectionId,
  mapping,
  onMappingChange,
  enabled = true,
}: UseShopifyProductPreviewArgs): ProductPreviewState {
  const [products, setProducts] = useState<ShopifySetupProductPreview[]>([]);
  const [matches, setMatches] = useState<Record<string, ShopifyMatchedJuststockProduct>>({});
  const [duplicateValues, setDuplicateValues] = useState<string[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mappingReady, setMappingReady] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const requestId = useRef(0);
  const skipNextFetch = useRef(false);
  const mappingReadyRef = useRef(mappingReady);
  const onMappingChangeRef = useRef(onMappingChange);
  mappingReadyRef.current = mappingReady;
  onMappingChangeRef.current = onMappingChange;

  const sourceIsMatchField = SHOPIFY_MATCH_SOURCE_FIELDS.has(mapping.sourceField);

  useEffect(() => {
    if (!enabled || !connectionId) return;
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (mappingReadyRef.current && !SHOPIFY_MATCH_SOURCE_FIELDS.has(mapping.sourceField)) return;

    const current = ++requestId.current;
    const ready = mappingReadyRef.current;
    setLoading(true);
    setError(null);
    setProducts([]);
    setMatches({});
    setDuplicateValues([]);
    setNextPageToken(null);
    setLoaded(false);
    container.shopifyConnectorRepository.getMatchPreview(connectionId, ready ? mapping : undefined)
      .then(response => {
        if (current !== requestId.current) return;
        if (response.status === 'error') {
          setError(response.error.message ?? 'Could not load the products');
          setLoaded(true);
          return;
        }
        setProducts(response.data.products);
        setMatches(response.data.matches);
        setDuplicateValues(response.data.duplicateValues);
        setNextPageToken(response.data.nextPageToken ?? null);
        setLoaded(true);
        if (!ready) {
          const nextMapping = response.data.mapping;
          if (nextMapping.sourceField !== mapping.sourceField || nextMapping.targetField !== mapping.targetField) {
            skipNextFetch.current = true;
          }
          onMappingChangeRef.current(nextMapping);
          setMappingReady(true);
        }
      })
      .finally(() => {
        if (current === requestId.current) setLoading(false);
      });
  }, [connectionId, enabled, mapping.sourceField, mapping.targetField]);

  const loadMore = async () => {
    if (!connectionId || !nextPageToken || loading) return;
    const current = requestId.current;
    setLoading(true);
    const response = await container.shopifyConnectorRepository.getMatchPreview(connectionId, mapping, nextPageToken);
    if (current !== requestId.current) {
      setLoading(false);
      return;
    }
    setLoading(false);
    if (response.status === 'error') {
      setError(response.error.message ?? 'Could not load the products');
      return;
    }
    const nextProducts = [...products, ...response.data.products];
    const duplicates = getDuplicateSourceValues(nextProducts, mapping.sourceField);
    setProducts(nextProducts);
    setMatches(currentMatches => ({ ...currentMatches, ...response.data.matches }));
    setDuplicateValues([...duplicates]);
    setNextPageToken(response.data.nextPageToken ?? null);
  };

  const activeDuplicates = sourceIsMatchField ? duplicateValues : [];
  const canImport = hasImportableProduct(products, mapping.sourceField, activeDuplicates) && loaded && !error;

  return {
    products,
    matches,
    duplicateValues: activeDuplicates,
    loading,
    error,
    hasMore: !!nextPageToken,
    loadMore,
    canImport,
    showNothingToImport: loaded && !error && !hasImportableProduct(products, mapping.sourceField, activeDuplicates),
  };
}

function hasImportableProduct(
  products: ShopifySetupProductPreview[],
  sourceField: string,
  duplicateValues: string[],
): boolean {
  const duplicates = new Set(duplicateValues);
  return products.some(product => {
    const value = product.fieldValues[sourceField]?.trim();
    return !!value && !duplicates.has(value);
  });
}
